import { useState } from "react";
import { addMonths, subMonths, addWeeks, subWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isToday, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, Clock, ListChecks } from "lucide-react";
import { api, useResource, COLORS } from "@/lib/api";
import { PageHeader, btnPrimary, btnSecondary, cardCls, CheckCircle } from "@/components/shared";
import { Switch } from "@/components/ui/switch";
import { EventDialog } from "@/components/calendar/EventDialog";
import { WeekView, weekLabel } from "@/components/calendar/WeekView";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const events = useResource("/events");
  const todos = useResource("/todo-lists");
  const [showTodos, setShowTodos] = useState(() => localStorage.getItem("cal-show-todos") !== "false");
  const [view, setViewState] = useState(() => localStorage.getItem("cal-view") || "month");
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dialog, setDialog] = useState(null);

  const isWeek = view === "week";
  const setView = (v) => {
    setViewState(v);
    localStorage.setItem("cal-view", v);
    setMonth(parseISO(selected));
  };
  const prev = () => setMonth(isWeek ? subWeeks(month, 1) : subMonths(month, 1));
  const next = () => setMonth(isWeek ? addWeeks(month, 1) : addMonths(month, 1));

  const toggleShow = (v) => {
    setShowTodos(v);
    localStorage.setItem("cal-show-todos", String(v));
  };
  const tasksOn = (d) => (showTodos ? todos.data.flatMap((l) => l.items.filter((i) => i.due_date === d).map((i) => ({ ...i, list: l }))) : []);
  const toggleTask = async (t) => todos.replace((await api.patch(`/todo-lists/${t.list.id}/items/${t.id}`, { done: !t.done })).data);

  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) });
  const byDate = (d) => events.data.filter((e) => e.date === d).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const dayEvents = byDate(selected);
  const dayTasks = tasksOn(selected);

  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Your days" title="Calendar" subtitle="Appointments, birthdays, bin day — everything in its place."
        action={<button data-testid="new-event-button" className={btnPrimary} onClick={() => setDialog({ date: selected })}><Plus className="h-4 w-4" /> New event</button>} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        <div className={`${cardCls} ${isWeek ? "xl:col-span-12" : "xl:col-span-8"} p-4 sm:p-6 fade-up`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="font-serif-display text-3xl font-semibold" data-testid="calendar-month-label">{isWeek ? weekLabel(month) : format(month, "MMMM yyyy")}</h2>
            <div className="flex bg-t-surface rounded-xl p-1">
              {["month", "week"].map((v) => (
                <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${view === v ? "bg-t-card shadow-sm text-t-ink" : "text-t-muted"}`}>
                  {v}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-t-c4 cursor-pointer">
              <Switch data-testid="calendar-show-todos-toggle" checked={showTodos} onCheckedChange={toggleShow} />
              Show to-do due dates
            </label>
            <div className="flex gap-2">
              <button data-testid="calendar-prev-month" className={btnSecondary} onClick={prev}><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="calendar-today" className={btnSecondary} onClick={() => { setMonth(new Date()); setSelected(format(new Date(), "yyyy-MM-dd")); }}>Today</button>
              <button data-testid="calendar-next-month" className={btnSecondary} onClick={next}><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          {isWeek ? (
            <WeekView cursor={month} selected={selected} onSelect={setSelected} byDate={byDate} tasksOn={tasksOn} showTodos={showTodos}
              onEventClick={(e) => setDialog({ event: e })} onAdd={(d) => setDialog({ date: d })} onToggleTask={toggleTask} />
          ) : (
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {WEEKDAYS.map((w) => <div key={w} className="text-[11px] font-semibold uppercase tracking-wider text-t-muted text-center pb-2">{w}</div>)}
            {days.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const evs = byDate(key);
              const tks = tasksOn(key);
              const active = key === selected;
              return (
                <button key={key} data-testid={`calendar-day-${key}`} onClick={() => setSelected(key)} onDoubleClick={() => setDialog({ date: key })}
                  className={`min-h-[64px] sm:min-h-[96px] p-1.5 sm:p-2 rounded-xl text-left border transition-[background-color,border-color] duration-200 flex flex-col ${
                    active ? "bg-t-primary-soft border-t-primary/60" : "bg-t-card/60 border-t-surface hover:border-t-line hover:bg-t-card"
                  } ${isSameMonth(d, month) ? "" : "opacity-40"}`}>
                  <span className={`text-xs sm:text-sm font-medium h-6 w-6 flex items-center justify-center rounded-full ${isToday(d) ? "bg-t-primary text-white" : ""}`}>{format(d, "d")}</span>
                  <div className="mt-1 space-y-1 w-full overflow-hidden">
                    {evs.slice(0, 2).map((e) => (
                      <div key={e.id} className="hidden sm:block truncate text-[11px] px-1.5 py-0.5 rounded-md text-white" style={{ backgroundColor: COLORS[e.color] || COLORS.terracotta }}>{e.title}</div>
                    ))}
                    {evs.length > 0 && <div className="sm:hidden flex gap-0.5">{evs.slice(0, 3).map((e) => <span key={e.id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: COLORS[e.color] }} />)}</div>}
                    {evs.length > 2 && <div className="hidden sm:block text-[10px] text-t-muted">+{evs.length - 2} more</div>}
                    {tks.slice(0, 2).map((t) => (
                      <div key={t.id} data-testid={`calendar-task-chip-${t.id}`}
                        className={`hidden sm:flex items-center gap-1 truncate text-[11px] px-1.5 py-0.5 rounded-md border border-dashed bg-t-card ${t.done ? "line-through text-t-faint" : "text-t-ink"}`}
                        style={{ borderColor: COLORS[t.list.color] || COLORS.terracotta }}>
                        <ListChecks className="h-3 w-3 shrink-0" style={{ color: COLORS[t.list.color] }} />
                        <span className="truncate">{t.text}</span>
                      </div>
                    ))}
                    {tks.length > 2 && <div className="hidden sm:block text-[10px] text-t-muted">+{tks.length - 2} tasks</div>}
                    {tks.length > 0 && <div className="sm:hidden h-1.5 w-1.5 rounded-sm border" style={{ borderColor: COLORS[tks[0].list.color] }} />}
                  </div>
                </button>
              );
            })}
          </div>
          )}
        </div>
        {!isWeek && (
        <div className={`${cardCls} xl:col-span-4 p-6 h-fit fade-up`} data-testid="day-detail-panel">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-t-primary">{format(parseISO(selected), "EEEE")}</p>
          <h3 className="font-serif-display text-3xl font-semibold mb-5" data-testid="day-detail-date">{format(parseISO(selected), "MMMM d, yyyy")}</h3>
          <div className="space-y-3">
            {dayEvents.map((e) => (
              <button key={e.id} data-testid={`event-item-${e.id}`} onClick={() => setDialog({ event: e })}
                className="w-full text-left flex gap-3 p-4 rounded-xl bg-t-bg border border-t-line hover:border-t-primary/40 transition-colors">
                <span className="w-1 rounded-full shrink-0" style={{ backgroundColor: COLORS[e.color] || COLORS.terracotta }} />
                <div className="min-w-0">
                  <p className="font-medium">{e.title}</p>
                  {e.time && <p className="text-xs text-t-muted flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{e.time}</p>}
                  {e.notes && <p className="text-xs text-t-muted mt-1 line-clamp-2">{e.notes}</p>}
                </div>
              </button>
            ))}
            {!dayEvents.length && !dayTasks.length && <p className="text-sm text-t-faint" data-testid="day-detail-empty">A quiet day. Nothing planned.</p>}
          </div>
          {dayTasks.length > 0 && (
            <div className="mt-6" data-testid="day-detail-tasks">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-t-c2 mb-3">Tasks due</p>
              <ul className="space-y-2">
                {dayTasks.map((t) => (
                  <li key={t.id} data-testid={`day-task-${t.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-t-card border border-t-line">
                    <CheckCircle checked={t.done} onClick={() => toggleTask(t)} color={COLORS[t.list.color]} testId={`day-task-check-${t.id}`} />
                    <span className={`flex-1 text-sm ${t.done ? "line-through text-t-faint" : ""}`}>{t.text}</span>
                    <span className="text-xs text-t-muted">{t.list.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button data-testid="add-event-for-day-button" onClick={() => setDialog({ date: selected })} className={`${btnSecondary} w-full mt-5`}><Plus className="h-4 w-4" /> Add event</button>
        </div>
        )}
      </div>
      <EventDialog state={dialog} onClose={() => setDialog(null)} onSaved={events.reload} />
    </div>
  );
}
